/* global URL */
// One deterministic renderer for the preview and the internal scheduled adapter.
// Calling this function conveys no sending authority; the adapter separately
// resolves the canonical shared mailbox and the server-held leased job.
export const BLIP_RENDERING_VERSION = 'BLIP_PROSPECT_V1'
export const BLIP_LOGO_URL = 'https://q7m2x9v4k8p6n3r1.avertsignal.com/brand/icon-192-navy.png'
export const BLIP_ADDRESS = '5900 Balcones Dr, Ste 100, Austin, TX 78731'
export const BLIP_OPT_OUT = "If you'd rather we didn't email you, just reply and let us know."
const signature = 'Blip\nAvertSignal’s AI assistant\nblip@avertsignal.com\navertsignal.com'
const footer = `${BLIP_ADDRESS}\n${BLIP_OPT_OUT}`
const escape = value => value.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char])
const urls = value => value.match(/(?:https?:\/\/|www\.)[^\s<>]+|\b[a-z0-9][a-z0-9.-]*\.(?:com|org|net|io|co|biz|us)(?:\/[^\s<>]*)?/gi) || []
const cleanUrl = value => value.replace(/[.,;:!?)]*$/, '')
const focusedLink = value => {
  try {
    const url = new URL(cleanUrl(value))
    return url.protocol === 'https:' && url.hostname === 'avertsignal.com' && !url.username && !url.password && !url.search && !url.hash && url.pathname !== '/' && !/(?:redirect|tracking|pixel)(?:\/|$)/i.test(url.pathname)
  } catch { return false }
}
function authoredBody(value) {
  let body = String(value || '').replace(/\r\n?/g, '\n').trim()
  // Only remove this exact generated terminal block, never guess at human
  // signatures or erase evidence from the middle of an authored message.
  const generated = `${signature}\n\n${footer}`
  for (;;) {
    const terminal = [generated, footer, signature].find(block => body.endsWith(block))
    if (!terminal) break
    body = body.slice(0, -terminal.length).trimEnd()
  }
  return body
}
export function validateBlipProspectContent({ firstBody, followupBody }) {
  const first = authoredBody(firstBody), followup = authoredBody(followupBody)
  if (!first || !followup || first.length > 18000 || followup.length > 18000) return 'EMAIL_TOO_LONG'
  if (urls(first).length) return 'FIRST_BODY_LINK_NOT_ALLOWED'
  const links = urls(followup)
  if (links.length !== 1 || !focusedLink(links[0])) return 'FOLLOWUP_DIRECT_LINK_REQUIRED'
  const question = value => (value.match(/[^.!?\n]+\?/g) || []).map(text => text.trim().toLowerCase().replace(/\s+/g, ' '))
  const firstQuestions = question(first), followupQuestions = question(followup)
  if (firstQuestions.length !== 1 || followupQuestions.length !== 1 || firstQuestions[0] === followupQuestions[0]) return 'DIFFERENT_QUESTION_REQUIRED'
  return null
}
export function renderBlipProspectEmail({ subject, body, step }) {
  if (!['FIRST', 'FOLLOWUP'].includes(step) || typeof subject !== 'string' || !subject.trim() || typeof body !== 'string' || !body.trim()) throw Error('invalid_prospect_rendering')
  const content = authoredBody(body)
  const bodyText = `${content}\n\n${signature}\n\n${footer}`
  if (bodyText.length > 20000) throw Error('invalid_prospect_rendering')
  const paragraphs = content.split(/\n\s*\n/).map(paragraph => {
    let html = escape(paragraph).replace(/\n/g, '<br>')
    // Authored text is never HTML. Only a direct, focused AvertSignal link
    // becomes clickable in a follow-up; no redirects or tracking decoration.
    if (step === 'FOLLOWUP') for (const raw of urls(paragraph)) if (focusedLink(raw)) {
      const url = cleanUrl(raw), safe = escape(url)
      html = html.replace(safe, `<a href="${safe}" style="color:#087b79;text-decoration:underline;">${safe}</a>`)
    }
    return `<p style="margin:0 0 16px;">${html}</p>`
  }).join('')
  const bodyHtml = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.6;color:#172638;max-width:600px;">${paragraphs}<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:22px 0 0;width:100%;max-width:420px;"><tr><td style="width:52px;vertical-align:top;padding:2px 12px 0 0;"><img src="${BLIP_LOGO_URL}" width="40" height="40" alt="AvertSignal circuit logo" style="display:block;border:0;width:40px;height:40px;"></td><td style="vertical-align:top;font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.5;color:#172638;"><strong style="font-size:15px;">Blip</strong><br>AvertSignal’s AI assistant<br><a href="mailto:blip@avertsignal.com" style="color:#087b79;text-decoration:none;">blip@avertsignal.com</a><br><a href="https://avertsignal.com/" style="color:#087b79;text-decoration:none;">avertsignal.com</a></td></tr></table><div style="margin-top:22px;padding-top:14px;border-top:1px solid #d5dfe5;font-family:Arial,Helvetica,sans-serif;font-size:12px;line-height:1.6;color:#536273;">${BLIP_ADDRESS}<br>${escape(BLIP_OPT_OUT)}</div></div>`
  return { subject: subject.trim(), bodyText, bodyHtml, renderingVersion: BLIP_RENDERING_VERSION }
}

// Only expose the controlled Blip prospect renderer's stored HTML to the CRM
// history preview. Inbound mail and human-authored messages stay plain text.
export function isTrustedBlipProspectHtml({ bodyHtml, senderEmail, direction }) {
  if (direction !== 'OUTBOUND' || String(senderEmail || '').trim().toLowerCase() !== 'blip@avertsignal.com') return false
  if (typeof bodyHtml !== 'string' || bodyHtml.length > 130000) return false
  return bodyHtml.includes(`src="${BLIP_LOGO_URL}"`)
    && bodyHtml.includes('AvertSignal’s AI assistant')
    && bodyHtml.includes(BLIP_ADDRESS)
    && bodyHtml.includes(escape(BLIP_OPT_OUT))
}
